import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { GiftIcon, TrophyIcon, StarIcon, ClockIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';

function Rewards() {
  const { user, updateUser } = useAuth();
  const [lastClaimDate, setLastClaimDate] = useState(null);
  const [canClaim, setCanClaim] = useState(false);
  const [timeUntilNextClaim, setTimeUntilNextClaim] = useState('');

  useEffect(() => {
    // Load last claim date from localStorage
    const savedLastClaim = localStorage.getItem('lastDailyRewardClaim');
    if (savedLastClaim) {
      setLastClaimDate(new Date(savedLastClaim));
    }
    checkClaimStatus();
  }, []);

  useEffect(() => {
    // Update claim status every minute
    const interval = setInterval(checkClaimStatus, 60000);
    return () => clearInterval(interval);
  }, []);

  const checkClaimStatus = () => {
    if (!lastClaimDate) {
      setCanClaim(true);
      return;
    }

    const now = new Date();
    const lastClaim = new Date(lastClaimDate);
    const timeDiff = now - lastClaim;
    const hoursDiff = timeDiff / (1000 * 60 * 60);

    if (hoursDiff >= 24) {
      setCanClaim(true);
      setTimeUntilNextClaim('');
    } else {
      setCanClaim(false);
      const remainingHours = 24 - hoursDiff;
      const hours = Math.floor(remainingHours);
      const minutes = Math.floor((remainingHours - hours) * 60);
      setTimeUntilNextClaim(`${hours}h ${minutes}m`);
    }
  };

  const handleClaimReward = async () => {
    if (!canClaim) return;

    try {
      const rewardAmount = 100; // Daily reward amount
      const updatedUser = {
        ...user,
        coins: (user?.coins || 0) + rewardAmount,
        totalCoinsEarned: (user?.totalCoinsEarned || 0) + rewardAmount,
        dailyRewardsClaimed: (user?.dailyRewardsClaimed || 0) + 1
      };

      await updateUser(updatedUser);
      setLastClaimDate(new Date());
      localStorage.setItem('lastDailyRewardClaim', new Date().toISOString());
      setCanClaim(false);
      toast.success(`Claimed ${rewardAmount} coins!`);
    } catch (error) {
      toast.error('Failed to claim reward. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-[#f56756] text-white p-6">
      <div className="max-w-4xl mx-auto space-y-6">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold text-[#402e2c]">Rewards</h1>
        </div>

        {/* Daily Reward Card */}
        <div className="bg-[#b34e42] rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-[#402e2c]/20 rounded-lg">
                <GiftIcon className="h-6 w-6 text-[#402e2c]" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-white">Daily Reward</h2>
                <p className="text-sm text-gray-400">Claim your daily reward of 100 coins</p>
              </div>
            </div>
            <button
              onClick={handleClaimReward}
              disabled={!canClaim}
              className={`px-6 py-2 rounded-full font-medium ${
                canClaim
                  ? 'bg-[#402e2c] text-[#f56756] hover:bg-[#402e2c]/90'
                  : 'bg-gray-600 text-gray-400 cursor-not-allowed'
              }`}
            >
              {canClaim ? 'Claim Now' : 'Claimed'}
            </button>
          </div>

          {!canClaim && timeUntilNextClaim && (
            <div className="flex items-center space-x-2 text-sm text-gray-400">
              <ClockIcon className="h-4 w-4" />
              <span>Next claim available in {timeUntilNextClaim}</span>
            </div>
          )}
        </div>

        {/* Rewards Progress */}
        <div className="bg-[#b34e42] rounded-xl p-6">
          <h3 className="text-lg font-semibold text-white mb-4">Your Progress</h3>
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-[#402e2c]/10 rounded-xl p-4">
              <p className="text-sm text-gray-400">Total Coins Earned</p>
              <p className="text-2xl font-semibold text-[#402e2c]">
                {user?.totalCoinsEarned || 0}
              </p>
            </div>
            <div className="bg-[#402e2c]/10 rounded-xl p-4">
              <p className="text-sm text-gray-400">Daily Rewards Claimed</p>
              <p className="text-2xl font-semibold text-[#402e2c]">
                {user?.dailyRewardsClaimed || 0}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Rewards; 